// Utility functions for dashboard

import dayjs from 'dayjs';

/**
 * Format number to Indonesian locale
 */
export const formatNumber = (num: number): string => {
  if (num === null || num === undefined) {
    return '0';
  }
  return num.toLocaleString('id-ID', { 
    minimumFractionDigits: 0,
    maximumFractionDigits: 0 
  });
};

export const formatNumber2Digit = (num: number): string => {
  if (num === null || num === undefined) {
    return '0,00';
  }
  return num.toLocaleString('id-ID', { 
    minimumFractionDigits: 0,
    maximumFractionDigits: 2 
  });
};

/**
 * Format percentage
 */
export const formatPercentage = (num: number): string => {
  if (num === null || num === undefined) {
    return '0,00%';
  }
  return num.toLocaleString('id-ID', { 
    minimumFractionDigits: 2,
    maximumFractionDigits: 2 
  }) + '%';
};

export const formatPercentageNumber = (num: number, decimals: number = 2): string => {
  if (num === 0 || num === null || num === undefined) {
    return '0,00';
  }
  
  return num.toLocaleString('id-ID', { 
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals 
  });
};

/**
 * Format date to Indonesian format
 */
export const formatDate = (dateString: string, format: string = 'DD/MM/YYYY'): string => {
  return dayjs(dateString).format(format);
};

/**
 * Format date for API calls (remove dashes)
 */
export const formatDateForApi = (dateString: string): string => {
  return dateString.replace(/-/g, '');
};

/**
 * Convert date to input format
 */
export const formatDateForInput = (dateString: string): string => {
  return dayjs(dateString).format('YYYY-MM-DD');
};

/**
 * Get current date in various formats
 */
export const getCurrentDate = () => {
  const now = dayjs();
  return {
    iso: now.format('YYYY-MM-DD'),
    api: now.format('YYYYMMDD'),
    display: now.format('DD/MM/YYYY'),
    long: now.format('dddd, DD MMMM YYYY')
  };
};

/**
 * Calculate percentage
 */
export const calculatePercentage = (part: number, total: number): number => {
  if (total === 0) return 0;
  return (part / total) * 100;
};

/**
 * Round to specified decimal places
 */
export const roundTo = (num: number, decimals: number = 2): number => {
  return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
};

/**
 * Check if value is numeric
 */
export const isNumeric = (value: any): boolean => {
  return !isNaN(value) && !isNaN(parseFloat(value));
};

/**
 * Safe division (returns 0 if divisor is 0)
 */
export const safeDivide = (dividend: number, divisor: number): number => {
  return divisor === 0 ? 0 : dividend / divisor;
};

/**
 * Get color for value based on thresholds
 */
export const getColorByValue = (value: number, thresholds: { good: number; warning: number }): string => {
  if (value >= thresholds.good) return '#22C55E'; // green
  if (value >= thresholds.warning) return '#F59E0B'; // orange
  return '#EF4444'; // red
};

/**
 * Truncate text to specified length
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
};

/**
 * Generate unique ID
 */
export const generateId = (): string => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

/**
 * Debounce function
 */
export const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Group array by key
 */
export const groupBy = <T>(array: T[], key: keyof T): Record<string, T[]> => {
  return array.reduce((result, item) => {
    const group = String(item[key]);
    if (!result[group]) {
      result[group] = [];
    }
    result[group].push(item);
    return result;
  }, {} as Record<string, T[]>);
};

/**
 * Sum array values by key
 */
export const sumBy = <T>(array: T[], key: keyof T): number => {
  return array.reduce((sum, item) => sum + Number(item[key]), 0);
};

/**
 * Get min/max values from array
 */
export const getMinMax = (array: number[]): { min: number; max: number } => {
  if (array.length === 0) return { min: 0, max: 0 };
  return {
    min: Math.min(...array),
    max: Math.max(...array)
  };
};

/**
 * Calculate average
 */
export const calculateAverage = (array: number[]): number => {
  if (array.length === 0) return 0;
  return array.reduce((sum, value) => sum + value, 0) / array.length;
};
